import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Pagination, Row, Spinner, Table } from 'react-bootstrap';
import api from '../../api';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';

interface EntidadeAuditoriaResumo {
  chave: string;
  nome: string;
}

interface AuditoriaEntidadeItem {
  revisaoId: number;
  dataModificacao: string;
  operacao: 'INSERT' | 'UPDATE' | 'DELETE' | 'UNKNOWN';
  atorTipo: 'USUARIO' | 'CONTA_SERVICO' | 'SISTEMA';
  atorId: string;
  atorNome: string;
  atorEmail?: string;
  ipAddress: string;
  entidade: string;
  entidadeId: string;
  dadosAuditados: Record<string, unknown>;
}

interface AuditoriaEntidadePageResponse {
  content: AuditoriaEntidadeItem[];
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  number: number;
  size: number;
}

const AuditoriaPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [entidades, setEntidades] = useState<EntidadeAuditoriaResumo[]>([]);
  const [entidadeSelecionada, setEntidadeSelecionada] = useState('');
  const [data, setData] = useState<AuditoriaEntidadePageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const [filtros, setFiltros] = useState({
    entidadeId: '',
    operacao: '',
    ator: '',
    dataInicio: '',
    dataFim: ''
  });

  useEffect(() => {
    setBreadcrumbs([
      { path: '/', breadcrumb: '🏠' },
      { path: '/auditoria', breadcrumb: 'Auditoria de Entidades' }
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const loadEntidades = async () => {
      try {
        const response = await api.get('/auditoria/entidades');
        const lista: EntidadeAuditoriaResumo[] = response.data || [];
        setEntidades(lista);
        if (lista.length > 0) {
          setEntidadeSelecionada(lista[0].chave);
        }
      } catch (e) {
        setErro('Erro ao carregar entidades de auditoria.');
      }
    };

    loadEntidades();
  }, []);

  const carregarAuditoria = async (pagina: number) => {
    if (!entidadeSelecionada) return;

    setLoading(true);
    setErro('');

    const params = new URLSearchParams({
      page: pagina.toString(),
      size: size.toString()
    });

    if (filtros.entidadeId) params.set('entidadeId', filtros.entidadeId);
    if (filtros.operacao) params.set('operacao', filtros.operacao);
    if (filtros.ator) params.set('ator', filtros.ator);
    if (filtros.dataInicio) params.set('dataInicio', filtros.dataInicio);
    if (filtros.dataFim) params.set('dataFim', filtros.dataFim);

    try {
      const response = await api.get(`/auditoria/entidades/${entidadeSelecionada}?${params}`);
      setData(response.data);
      setPage(pagina);
    } catch (e) {
      setErro('Erro ao carregar registros de auditoria.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (entidadeSelecionada) {
      carregarAuditoria(0);
    }
  }, [entidadeSelecionada, size]);

  const handleBuscar = () => {
    carregarAuditoria(0);
  };

  const toggleExpand = (revisionId: number) => {
    setExpandedRows((prev) => ({ ...prev, [revisionId]: !prev[revisionId] }));
  };

  const formatarData = (valor?: string) => {
    if (!valor) return '-';
    return new Date(valor).toLocaleString('pt-BR');
  };

  const badgeOperacao = (operacao: string) => {
    if (operacao === 'INSERT') return 'success';
    if (operacao === 'UPDATE') return 'warning';
    if (operacao === 'DELETE') return 'danger';
    return 'secondary';
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-3">Auditoria de Entidades</h2>

      <Alert variant="info" className="mb-3">
        Consulta unificada das revisoes Envers por entidade, com filtros por periodo, operacao e ator.
      </Alert>

      <Card className="mb-3">
        <Card.Header><strong>Filtros</strong></Card.Header>
        <Card.Body>
          <Row className="g-2">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Entidade</Form.Label>
                <Form.Select
                  size="sm"
                  value={entidadeSelecionada}
                  onChange={(e) => setEntidadeSelecionada(e.target.value)}
                >
                  {entidades.map((entidade) => (
                    <option key={entidade.chave} value={entidade.chave}>{entidade.nome}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small">ID Entidade</Form.Label>
                <Form.Control
                  size="sm"
                  value={filtros.entidadeId}
                  onChange={(e) => setFiltros((prev) => ({ ...prev, entidadeId: e.target.value }))}
                  placeholder="UUID ou ID"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small">Operacao</Form.Label>
                <Form.Select
                  size="sm"
                  value={filtros.operacao}
                  onChange={(e) => setFiltros((prev) => ({ ...prev, operacao: e.target.value }))}
                >
                  <option value="">Todas</option>
                  <option value="INSERT">INSERT</option>
                  <option value="UPDATE">UPDATE</option>
                  <option value="DELETE">DELETE</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small">Ator</Form.Label>
                <Form.Control
                  size="sm"
                  value={filtros.ator}
                  onChange={(e) => setFiltros((prev) => ({ ...prev, ator: e.target.value }))}
                  placeholder="Nome, email ou ID"
                />
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Group>
                <Form.Label className="small">Inicio</Form.Label>
                <Form.Control
                  size="sm"
                  type="datetime-local"
                  value={filtros.dataInicio}
                  onChange={(e) => setFiltros((prev) => ({ ...prev, dataInicio: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Group>
                <Form.Label className="small">Fim</Form.Label>
                <Form.Control
                  size="sm"
                  type="datetime-local"
                  value={filtros.dataFim}
                  onChange={(e) => setFiltros((prev) => ({ ...prev, dataFim: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={1} className="d-flex align-items-end">
              <Button size="sm" variant="primary" onClick={handleBuscar} disabled={loading || !entidadeSelecionada}>
                Buscar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {erro && <Alert variant="danger">{erro}</Alert>}

      {loading && (
        <div className="text-center my-4">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && data && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small>Total: {data.totalElements}</small>
            <Form.Select
              size="sm"
              style={{ width: 120 }}
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
            >
              <option value={10}>10/pg</option>
              <option value={20}>20/pg</option>
              <option value={50}>50/pg</option>
            </Form.Select>
          </div>

          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Data/Hora</th>
                <th>Operacao</th>
                <th>Entidade ID</th>
                <th>Ator</th>
                <th>IP</th>
                <th>Revisao</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {data.content.map((item) => (
                <React.Fragment key={item.revisaoId}>
                  <tr>
                    <td>{formatarData(item.dataModificacao)}</td>
                    <td><Badge bg={badgeOperacao(item.operacao)}>{item.operacao}</Badge></td>
                    <td>{item.entidadeId}</td>
                    <td>
                      <div><strong>{item.atorNome || '-'}</strong></div>
                      <small>{item.atorTipo} | {item.atorId || '-'}</small>
                      {item.atorEmail && <div><small>{item.atorEmail}</small></div>}
                    </td>
                    <td>{item.ipAddress || '-'}</td>
                    <td>{item.revisaoId}</td>
                    <td>
                      <Button size="sm" variant="outline-secondary" onClick={() => toggleExpand(item.revisaoId)}>
                        {expandedRows[item.revisaoId] ? 'Ocultar' : 'Detalhes'}
                      </Button>
                    </td>
                  </tr>
                  {expandedRows[item.revisaoId] && (
                    <tr>
                      <td colSpan={7}>
                        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {JSON.stringify(item.dadosAuditados, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {data.content.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center">Nenhum registro encontrado.</td>
                </tr>
              )}
            </tbody>
          </Table>

          <Pagination>
            <Pagination.First onClick={() => carregarAuditoria(0)} disabled={data.first || loading} />
            <Pagination.Prev onClick={() => carregarAuditoria(page - 1)} disabled={data.first || loading} />
            <Pagination.Item active>{page + 1}</Pagination.Item>
            <Pagination.Next onClick={() => carregarAuditoria(page + 1)} disabled={data.last || loading} />
            <Pagination.Last onClick={() => carregarAuditoria(Math.max(data.totalPages - 1, 0))} disabled={data.last || loading} />
          </Pagination>
        </>
      )}
    </div>
  );
};

export default AuditoriaPage;
